import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import Logo02 from "../../assets/images/logo-02.png";
import AppButton from "../../components/ui/AppButton";
import AppInputField from "../../components/ui/AppInputField";
import { Spinner } from "../../components/ui/common";
import SplitScreenLayout from "../../components/views/layouts/SplitScreenLayout";
import { useToast } from "../../hooks/useToast";
import { useLoginMutation } from "./authApiSlice";
import { setCredentials } from "./authSlice";

const signInSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type SignInFormData = z.infer<typeof signInSchema>;

const AdminSignIn: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPage = location.state?.from || "/super-admin";
  const dispatch = useDispatch();
  const [login, { isLoading }] = useLoginMutation();
  const toast = useToast();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    mode: "onBlur",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: SignInFormData) => {
    try {
      const response = await login({
        email: data.email,
        password: data.password,
      }).unwrap();

      dispatch(
        setCredentials({
          accessToken: response?.data?.token,
          user: response?.data?.userDetails,
          permissions: response?.data?.permissions,
        })
      );

      const roles: string[] = response?.data?.userDetails?.roles ?? [];
      const isSuperAdmin = roles.some(
        (role) => role.toLowerCase() === "superadmin"
      );

      navigate(isSuperAdmin ? "/super-admin" : "/admin", { replace: true });

      toast.success({
        title: "Sign In Success!",
        message: response?.message,
      });
      reset();
    } catch (error: any) {
      toast.error({
        title: "Sign In Failed",
        message: error?.data?.message ?? "Sign In failed",
      });
      console.error("Login error:", error);
    }
  };

  const inputFields = [
    {
      label: "Email",
      name: "email" as const,
      type: "email",
    },
    {
      label: "Password",
      name: "password" as const,
      type: "password",
    },
  ];

  return (
    <SplitScreenLayout>
      <div className="flex items-center justify-center w-full">
        <div className="w-full max-w-md sm:max-w-lg lg:max-w-xl xl:max-w-2xl">
          {/* Logo and Header */}
          <div className="flex flex-col items-center justify-center mb-8 sm:mb-10 lg:mb-14">
            <img
              src={Logo02}
              alt="Logo"
              className="w-16 h-16 sm:w-20 sm:h-20 mb-3 sm:mb-4"
            />
            <h2 className="text-base sm:text-lg lg:text-xl text-pColor my-1 sm:my-2">
              Welcome 👋
            </h2>
            <h1 className="text-primary font-semibold text-xl sm:text-2xl lg:text-[28px]">
              Fleetpro Admin
            </h1>
          </div>

          {/* Form Section */}
          <div className="space-y-4 sm:space-y-6 text-base sm:text-lg font-medium">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-3 sm:space-y-4"
            >
              {inputFields.map(({ label, name, type }) => (
                <Controller
                  key={name}
                  control={control}
                  name={name}
                  render={({ field }) => (
                    <AppInputField
                      label={label}
                      type={type}
                      error={errors[name]?.message}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      className="bg-[#F6F6F6] border border-[#F6F6F6] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 h-11 sm:h-12 text-hColor"
                    />
                  )}
                />
              ))}

              <div className="flex justify-end">
                <AppButton
                  onClick={() => navigate("/forgot-password")}
                  disabled={isLoading}
                  variant="link"
                  className="text-xs sm:text-sm font-medium text-pColor hover:text-primary"
                >
                  Forgot password?
                </AppButton>
              </div>

              <AppButton
                type="submit"
                disabled={isLoading}
                className="w-full h-11 sm:h-12 text-base sm:text-lg font-medium"
              >
                {isLoading ? <Spinner /> : "Sign In"}
              </AppButton>

              {/* Register Link */}
              <div className="text-center pt-3 sm:pt-4">
                <p className="text-sm sm:text-base text-hColor">
                  Don't have an account?{" "}
                  <button
                    type="button"
                    className="text-teal-600 hover:text-teal-700 font-medium transition-colors"
                    onClick={() => navigate("/sign-up")}
                  >
                    Register
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </SplitScreenLayout>
  );
};

export default AdminSignIn;
